import { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { initPostHog, identifyUser } from './lib/posthog';
import { ErrorBoundary } from './components/ErrorBoundary';

// Hooks
import { useDeviceId } from './hooks/useDeviceId';
import { useOnboarding } from './hooks/useOnboarding';
import { useDashboard } from './hooks/useDashboard';
import { useAskBirdie } from './hooks/useAskBirdie';

// Contexts
import { AppProviders, useAnalysisContext, useSwingHistoryContext } from './contexts';

// Components
import { Header, CreditWarningBanners } from './components/layout';
import { AuthModal } from './components/auth';
import { PricingModal } from './components/billing';
import { AnalysisHero, FullAnalysis } from './components/analysis';
import { AskBirdiePanel } from './components/coaching';
import { LandingPage, OnboardingModal } from './components/landing';
import { Dashboard } from './components/dashboard/Dashboard';
import { ComparisonModal } from './components/history/ComparisonModal';

function AppContent() {
  const deviceId = useDeviceId();

  // Initialize PostHog after first render (deferred)
  useEffect(() => {
    initPostHog();
    if (deviceId) {
      identifyUser(deviceId);
    }
  }, [deviceId]);

  // Core hooks - use context for swing history (single instance)
  const onboarding = useOnboarding();
  const swingHistory = useSwingHistoryContext();
  const dashboard = useDashboard(deviceId, swingHistory.swingHistory);

  // Get analysis context for current swing state
  const analysis = useAnalysisContext();

  // Ask Birdie hook
  const askBirdie = useAskBirdie(
    deviceId,
    analysis.currentSwingId,
    analysis.analysisResult
  );

  const showLanding = !analysis.analysisResult && !(dashboard.showDashboard && dashboard.dashboardData);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Header */}
      <Header
        swingHistory={swingHistory.swingHistory}
        showDashboard={dashboard.showDashboard}
        fetchDashboard={dashboard.fetchDashboard}
        setShowDashboard={dashboard.setShowDashboard}
      />

      {/* Credit Warning Banners - hidden when viewing analysis results */}
      {!analysis.analysisResult && <CreditWarningBanners />}

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Dashboard Loading */}
        {!analysis.analysisResult && dashboard.dashboardLoading && (
          <div className="py-20 text-center">
            <div className="h-12 w-12 animate-spin text-emerald-400 mx-auto mb-4 border-4 border-emerald-400 border-t-transparent rounded-full" />
            <p className="text-slate-400">Loading your golf journey...</p>
          </div>
        )}

        {/* Dashboard View */}
        {!analysis.analysisResult && !dashboard.dashboardLoading && dashboard.showDashboard && dashboard.dashboardData && (
          <Dashboard
            dashboardData={dashboard.dashboardData}
            setShowDashboard={dashboard.setShowDashboard}
          />
        )}

        {/* Landing Page */}
        {showLanding && <LandingPage />}

        {/* Analysis Results */}
        {analysis.analysisResult && (
          <div className="py-12">
            {!analysis.showFullAnalysis ? (
              <AnalysisHero
                swingHistory={swingHistory.swingHistory}
              />
            ) : (
              <FullAnalysis
                swingHistory={swingHistory.swingHistory}
                openAskBirdie={askBirdie.openAskBirdie}
                setShowComparison={swingHistory.setShowComparison}
              />
            )}
          </div>
        )}

        {/* Comparison Modal */}
        <ComparisonModal
          showComparison={swingHistory.showComparison}
          setShowComparison={swingHistory.setShowComparison}
          analysisResult={analysis.analysisResult}
          swingHistory={swingHistory.swingHistory}
        />

        {/* Ask Birdie Panel */}
        <AskBirdiePanel
          showAskBirdie={askBirdie.showAskBirdie}
          setShowAskBirdie={askBirdie.setShowAskBirdie}
          askBirdieMessages={askBirdie.askBirdieMessages}
          askBirdieInput={askBirdie.askBirdieInput}
          setAskBirdieInput={askBirdie.setAskBirdieInput}
          askBirdieLoading={askBirdie.askBirdieLoading}
          questionsRemaining={askBirdie.questionsRemaining}
          sendMessage={askBirdie.sendMessage}
          getSuggestedQuestions={askBirdie.getSuggestedQuestions}
          isReady={askBirdie.isReady}
        />
      </main>

      {/* Modals */}
      <PricingModal />
      <AuthModal />
      <OnboardingModal
        showOnboarding={onboarding.showOnboarding}
        completeOnboarding={onboarding.completeOnboarding}
      />

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 text-center text-sm text-slate-500">
          <p>© 2026 SwingSlot • Powered by Birdie</p>
        </div>
      </footer>

      {/* Vercel Analytics & Speed Insights */}
      <Analytics />
      <SpeedInsights />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProviders>
        <AppContent />
      </AppProviders>
    </ErrorBoundary>
  );
}
